/**
 * Geometry Engine — pure functions for computing link line positions,
 * viewport clipping, and direction indicator spacing.
 *
 * Requirements: 3.3, 5.1, 5.4, 5.5, 5.6
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

// ---------------------------------------------------------------------------
// computeCenter
// ---------------------------------------------------------------------------

/** Compute the center of a bounding rectangle. */
export function computeCenter(rect: Rect): Point {
  return {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2,
  };
}

// ---------------------------------------------------------------------------
// clipToViewport
// ---------------------------------------------------------------------------

/**
 * Clip a line from source to target against a viewport boundary.
 * Returns the point on the viewport edge where the line exits,
 * or the target point if it's inside the viewport.
 */
export function clipToViewport(
  source: Point,
  target: Point,
  viewport: Rect,
): { point: Point; isClipped: boolean } {
  const vLeft = viewport.x;
  const vRight = viewport.x + viewport.width;
  const vTop = viewport.y;
  const vBottom = viewport.y + viewport.height;

  // If target is inside the viewport, no clipping needed
  if (
    target.x >= vLeft &&
    target.x <= vRight &&
    target.y >= vTop &&
    target.y <= vBottom
  ) {
    return { point: target, isClipped: false };
  }

  // Find intersection of line (source → target) with viewport edges.
  // Use parametric form: P(t) = source + t * (target - source), t ∈ [0, 1]
  const dx = target.x - source.x;
  const dy = target.y - source.y;

  let tMin = 0;
  let tMax = 1;

  // Clip against each edge using Liang-Barsky algorithm
  const clips = [
    { p: -dx, q: -(vLeft - source.x) },   // left
    { p: dx, q: vRight - source.x },       // right
    { p: -dy, q: -(vTop - source.y) },     // top
    { p: dy, q: vBottom - source.y },       // bottom
  ];

  for (const { p, q } of clips) {
    if (p === 0) {
      // Line is parallel to this edge
      if (q < 0) {
        // Line is outside this edge entirely — shouldn't happen since source is inside
        return { point: target, isClipped: true };
      }
      continue;
    }

    const t = q / p;
    if (p < 0) {
      // Line enters from outside
      tMin = Math.max(tMin, t);
    } else {
      // Line exits to outside
      tMax = Math.min(tMax, t);
    }
  }

  // The exit point is at tMax (since source is inside, line exits at tMax)
  const clippedX = source.x + tMax * dx;
  const clippedY = source.y + tMax * dy;

  return {
    point: { x: clippedX, y: clippedY },
    isClipped: true,
  };
}

// ---------------------------------------------------------------------------
// nearestEdge
// ---------------------------------------------------------------------------

/** Determine which viewport edge a point is closest to. */
export function nearestEdge(
  point: Point,
  viewport: Rect,
): 'top' | 'bottom' | 'left' | 'right' {
  const distTop = Math.abs(point.y - viewport.y);
  const distBottom = Math.abs(point.y - (viewport.y + viewport.height));
  const distLeft = Math.abs(point.x - viewport.x);
  const distRight = Math.abs(point.x - (viewport.x + viewport.width));

  const min = Math.min(distTop, distBottom, distLeft, distRight);

  if (min === distTop) return 'top';
  if (min === distBottom) return 'bottom';
  if (min === distLeft) return 'left';
  return 'right';
}

// ---------------------------------------------------------------------------
// spaceIndicators
// ---------------------------------------------------------------------------

/**
 * Space indicators on an edge so they're >= minGap apart.
 * Points are sorted by their position along the edge and then
 * spread apart if they're closer than minGap.
 */
export function spaceIndicators(
  points: Point[],
  edge: 'top' | 'bottom' | 'left' | 'right',
  minGap: number,
): Point[] {
  if (points.length <= 1) return [...points];

  const isHorizontal = edge === 'top' || edge === 'bottom';

  // Sort by position along the edge axis
  const sorted = [...points].sort((a, b) =>
    isHorizontal ? a.x - b.x : a.y - b.y,
  );

  // Spread points apart to ensure minGap between adjacent ones
  const result: Point[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const prev = result[i - 1];
    const current = sorted[i];

    if (isHorizontal) {
      const distance = current.x - prev.x;
      if (distance < minGap) {
        result.push({ x: prev.x + minGap, y: current.y });
      } else {
        result.push(current);
      }
    } else {
      const distance = current.y - prev.y;
      if (distance < minGap) {
        result.push({ x: current.x, y: prev.y + minGap });
      } else {
        result.push(current);
      }
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// computeEdgeAnchor
// ---------------------------------------------------------------------------

/**
 * Compute the anchor point on a rectangle's edge closest to a target point.
 * Returns the midpoint of the edge facing the target direction.
 *
 * For example, if the target is to the right of the rect, returns the
 * midpoint of the right edge. If the target is above and to the left,
 * returns the midpoint of whichever edge (top or left) the target is
 * more strongly aligned with.
 */
export function computeEdgeAnchor(rect: Rect, target: Point): Point {
  const center = computeCenter(rect);
  const dx = target.x - center.x;
  const dy = target.y - center.y;

  // Avoid division by zero — if target is at the center, use right edge
  if (dx === 0 && dy === 0) {
    return { x: rect.x + rect.width, y: center.y };
  }

  // Determine dominant direction by comparing normalized distances
  // Use aspect ratio to decide whether horizontal or vertical edge dominates
  const halfW = rect.width / 2;
  const halfH = rect.height / 2;

  // Normalized direction: how far along each axis relative to the half-dimension
  const nx = halfW > 0 ? Math.abs(dx) / halfW : 0;
  const ny = halfH > 0 ? Math.abs(dy) / halfH : 0;

  if (nx >= ny) {
    // Horizontal edge dominates — use left or right edge midpoint
    if (dx >= 0) {
      return { x: rect.x + rect.width, y: center.y };
    }
    return { x: rect.x, y: center.y };
  }
  // Vertical edge dominates — use top or bottom edge midpoint
  if (dy >= 0) {
    return { x: center.x, y: rect.y + rect.height };
  }
  return { x: center.x, y: rect.y };
}
